import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Button,
  Box,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { AdminUser } from '@/types/domain';

export interface AdminUserEditDialogProps {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
  onSuccess: () => void;
  error: string | null;
  onUpdate: (id: number, data: { password?: string; email?: string }) => Promise<void>;
  clearError: () => void;
}

export function AdminUserEditDialog({
  open,
  user,
  onClose,
  onSuccess,
  error,
  onUpdate,
  clearError,
}: AdminUserEditDialogProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');

  // 当用户变化时重置表单
  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setPassword('');
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!user) return;

    setLocalError('');
    clearError();

    const updateData: { password?: string; email?: string } = {};
    if (password) {
      updateData.password = password;
    }
    if (email) {
      updateData.email = email;
    }

    try {
      await onUpdate(user.id, updateData);
      handleClose();
      onSuccess();
    } catch (err: any) {
      setLocalError(err.message || t('users.updateFailed'));
    }
  };

  const handleClose = () => {
    setPassword('');
    setEmail('');
    setLocalError('');
    clearError();
    onClose();
  };

  const displayError = localError || error;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon />
          {t('users.editTitle')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {displayError && <Alert severity="error">{displayError}</Alert>}

          <TextField
            fullWidth
            label={t('users.username')}
            value={user?.username || ''}
            disabled
            helperText={t('users.usernameNotChangeable')}
          />

          <TextField
            fullWidth
            label={t('users.newPasswordOptional')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={t('users.passwordKeepCurrent')}
          />

          <TextField
            fullWidth
            label={t('users.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('users.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained">
          {t('users.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
